import { IntegrationsSyncableStateStatusEnum } from 'generated/resources/graphql'

export type AggregatedStatus =
  | 'syncing'
  | 'pending'
  | 'outdated'
  | 'partiallySynced'
  | 'failed'
  | 'synced'

export type AggregatableStatuses =
  | (IntegrationsSyncableStateStatusEnum | null)[]
  | IntegrationsSyncableStateStatusEnum
  | null
  | undefined

type NormalizedStatuses = Array<IntegrationsSyncableStateStatusEnum | undefined>

const normalizeAggregatedStatuses = (
  statuses: Exclude<AggregatableStatuses, null | undefined>
): NormalizedStatuses =>
  Array.isArray(statuses) ? statuses.map((status) => status ?? undefined) : [statuses]

export const aggregateStatuses = (statuses: AggregatableStatuses): AggregatedStatus | null => {
  // Convention:
  // null means pending
  // undefined means showing "-" instead of an icon

  if (statuses === null) return 'pending'
  if (statuses === undefined) return null
  if (Array.isArray(statuses) && statuses.length === 0) return null

  const normalizedStatuses = normalizeAggregatedStatuses(statuses)
  // the order of the conditions is important
  if (isPending(normalizedStatuses)) return 'pending'
  if (isSyncing(normalizedStatuses)) return 'syncing'
  if (isOutdated(normalizedStatuses)) return 'outdated'
  if (isPartiallySynced(normalizedStatuses)) return 'partiallySynced'
  if (hasFailed(normalizedStatuses)) return 'failed'
  return 'synced'
}

const isSyncing = (syncableStates: NormalizedStatuses) => syncableStates.includes('syncing')

const isPending = (syncableStates: NormalizedStatuses) =>
  syncableStates.every((syncableState) => syncableState === undefined)

const isOutdated = (syncableStates: NormalizedStatuses) =>
  syncableStates.includes('outdated') ||
  (syncableStates.some((syncableState) => syncableState === undefined) &&
    syncableStates.some((syncableState) => syncableState !== undefined))

const hasFailed = (syncableStates: NormalizedStatuses) =>
  syncableStates.includes('failed') || syncableStates.includes('invalid')

const isPartiallySynced = (syncableStates: NormalizedStatuses) =>
  syncableStates.includes('synced') && hasFailed(syncableStates)
